import type {Command, EditorView} from '@codemirror/view'
import type {Link} from 'mdast'

import {createText} from '../../extensions/single-line-editor'
import {findWordBoundaries, hasNonEmptySelection} from '../../utils'
import {getExistingFormatting, removeFormattingAtSelection} from './utils'

// Check if a Link node is a link with both [label] and (resource) delimiters
export const isLabeledLink = (node: Link): boolean => {
  const hasLabelMarker = node?.children?.some(n => n.type === 'delimiter' && n.tokenType === 'labelMarker')
  const hasResource = node?.children?.some(n => n.type === 'delimiter' && n.tokenType === 'resource')
  return hasLabelMarker && hasResource
}

export const formatLink: Command = (view: EditorView) => {
  if (!view.hasFocus) view.focus()

  const selection = view.state.selection.main
  const linkNode = getExistingFormatting(view, 'link') as Link | undefined

  // Remove existing [text](url) formatted link if present
  if (linkNode && isLabeledLink(linkNode)) {
    removeFormattingAtSelection(view, linkNode)
    return true
  }

  // Determine range to format (selected text or word at cursor)
  const isTextSelected = hasNonEmptySelection(view.state)
  const {start, end} = isTextSelected
    ? {start: selection.from, end: selection.to}
    : findWordBoundaries(view.state.doc.toString(), selection.anchor)

  let prefix
  let suffix
  let cursorPosition

  // Add link formatting: wrap autolink URL as [](url) or wrap text as [text]()
  const isAutolink = !!linkNode // Autolink (e.g., https://github.com or <https://github.com>) without []() syntax
  if (isAutolink) {
    prefix = '[]('
    suffix = ')'
    cursorPosition = start + 1 // Position cursor inside the square brackets for autolinks
  } else {
    prefix = '['
    suffix = ']()'
    cursorPosition = end + 3 // Position cursor inside the parentheses by default
  }

  view.dispatch({
    changes: [
      {from: start, insert: createText(prefix)},
      {from: end, insert: createText(suffix)},
    ],
    selection: {anchor: cursorPosition},
    userEvent: 'input',
    scrollIntoView: true,
  })

  return true
}
